import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Clock, ExternalLink, Menu, X } from "lucide-react";
import SEO from "../components/SEO";
import "../home.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catColors = {
  reflections: { bg: "var(--green-l)", color: "var(--green)" },
  "field-notes": { bg: "var(--coral-l)", color: "var(--coral)" },
  policy: { bg: "var(--blue-l)", color: "var(--blue)" },
  "initiative-updates": { bg: "var(--amber-l)", color: "var(--amber)" },
};

function formatDate(p) {
  if (p?.display_date) return p.display_date;
  return p?.created_at ? new Date(p.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";
}

function readTime(content) {
  const words = content?.split(/\s+/).length || 0;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
}

/* ── Sub-components ── */

function SharedNav({ scrolled, mobileOpen, setMobileOpen }) {
  return (
    <>
      <header className={`hm-nav ${scrolled ? "scrolled" : ""}`} data-testid="blogd-nav">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="hm-display text-xl tracking-tight" style={{ color: "var(--ink)", textDecoration: "none" }}>
            Tulsi <span style={{ color: "var(--green)" }}>Giri</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="hm-body text-sm font-medium transition-colors" style={{ color: "var(--ink3)", textDecoration: "none" }}>Home</Link>
            <Link to="/blog" className="hm-body text-sm font-semibold transition-colors" style={{ color: "var(--ink)", textDecoration: "none" }}>Updates</Link>
          </nav>
          <Link to="/#contact" className="hm-btn-filled hidden md:inline-flex" style={{ padding: "8px 22px", fontSize: "0.85rem", textDecoration: "none" }}>Let's Connect</Link>
          <button className="md:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink)" }}><Menu size={24} /></button>
        </div>
      </header>
      <div className={`hm-overlay ${mobileOpen ? "open" : ""}`}>
        <button onClick={() => setMobileOpen(false)} aria-label="Close menu" style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", cursor: "pointer", color: "var(--ink)" }}><X size={28} /></button>
        <Link to="/" className="hm-display text-2xl" style={{ color: "var(--ink)", textDecoration: "none" }}>Home</Link>
        <Link to="/blog" onClick={() => setMobileOpen(false)} className="hm-display text-2xl" style={{ color: "var(--ink)", textDecoration: "none" }}>Updates</Link>
      </div>
    </>
  );
}

function SharedFooter() {
  return (
    <footer className="py-8" style={{ background: "var(--ink)" }}>
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.8rem" }}>&copy; 2026 Tulsi Giri</p>
        <div className="flex items-center gap-4" style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.8rem" }}>
          <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>Home</Link>
          <Link to="/blog" style={{ color: "inherit", textDecoration: "none" }}>Updates</Link>
          <Link to="/admin" style={{ color: "inherit", textDecoration: "none" }}>Admin</Link>
        </div>
      </div>
    </footer>
  );
}

function ArticleHeader({ post, postId }) {
  return (
    <section className="py-10 md:py-14" style={{ background: "var(--sky)" }} data-testid="blogd-header">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/blog" className="inline-flex items-center gap-2 mb-6" style={{ color: "var(--ink3)", textDecoration: "none", fontSize: "0.85rem" }} data-testid="blogd-back-top">
          <ArrowLeft size={14} /> Back to Updates
        </Link>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {post.category && (
            <span className="hm-tag" style={{ background: catColors[post.category]?.bg || "var(--sky)", color: catColors[post.category]?.color || "var(--ink3)" }}>
              {post.category.replace("-", " ")}
            </span>
          )}
          <span className="flex items-center gap-1" style={{ fontSize: "0.8rem", color: "var(--ink3)" }}>
            <Calendar size={12} /> {formatDate(post)}
          </span>
          <span className="flex items-center gap-1" style={{ fontSize: "0.8rem", color: "var(--ink3)" }}>
            <Clock size={12} /> {readTime(post.content)}
          </span>
        </div>
        <h1 className="hm-display" style={{ fontSize: "clamp(1.6rem, 4vw, 2.4rem)", fontWeight: 700, color: "var(--ink)", lineHeight: 1.2 }} data-testid="blogd-title">{post.title}</h1>
        {post.excerpt && (
          <p className="mt-4" style={{ color: "var(--ink3)", fontSize: "1.05rem", lineHeight: 1.6, fontStyle: "italic" }} data-testid="blogd-excerpt">{post.excerpt}</p>
        )}
      </div>
    </section>
  );
}

function ArticleBody({ post }) {
  const renderBlock = (block, idx) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    const key = `block-${idx}-${trimmed.substring(0, 20)}`;
    if (trimmed.startsWith("## ")) {
      return <h2 key={key} className="hm-display" style={{ fontSize: "1.4rem", fontWeight: 600, color: "var(--ink)", marginTop: 32, marginBottom: 12 }}>{trimmed.replace("## ", "")}</h2>;
    }
    if (trimmed.startsWith("> ")) {
      return <blockquote key={key} style={{ borderLeft: "3px solid var(--green)", paddingLeft: 20, margin: "24px 0", fontStyle: "italic", color: "var(--ink2)" }}>{trimmed.replace("> ", "")}</blockquote>;
    }
    return <p key={key} style={{ color: "var(--ink2)", lineHeight: 1.85, marginBottom: 16 }}>{trimmed}</p>;
  };

  return (
    <section className="py-8 md:py-12" data-testid="blogd-body">
      <div className="max-w-3xl mx-auto px-6">
        <article>{post.content?.split("\n\n").map(renderBlock)}</article>
        {post.source_url && (
          <div className="mt-8 pt-6" style={{ borderTop: "1px solid var(--div)" }}>
            <a href={post.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2" style={{ color: "var(--blue)", textDecoration: "none", fontSize: "0.9rem", fontWeight: 500 }} data-testid="blogd-source-link">
              View Original Source <ExternalLink size={14} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function RelatedPosts({ related }) {
  if (related.length === 0) return null;
  return (
    <div data-testid="blogd-related">
      <p className="hm-mono mb-4" style={{ fontSize: "0.65rem", color: "var(--ink3)", letterSpacing: "0.12em", textTransform: "uppercase" }}>You might also like</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {related.map((rp) => (
          <Link
            key={rp.id}
            to={`/blog/${rp.id}`}
            style={{ textDecoration: "none", color: "inherit", border: "1px solid var(--div)", borderRadius: 12, overflow: "hidden", background: "var(--white)", transition: "box-shadow 300ms ease" }}
            data-testid={`blogd-related-${rp.id}`}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
          >
            {rp.image_url && (
              <div style={{ height: 140, overflow: "hidden" }}>
                <img src={rp.image_url} alt={rp.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
              </div>
            )}
            <div style={{ padding: 16 }}>
              {rp.category && (
                <span className="hm-tag" style={{ background: catColors[rp.category]?.bg || "var(--sky)", color: catColors[rp.category]?.color || "var(--ink3)", marginBottom: 8, display: "inline-block" }}>
                  {rp.category.replace("-", " ")}
                </span>
              )}
              <h4 className="hm-display" style={{ fontSize: "1rem", fontWeight: 600, color: "var(--ink)", lineHeight: 1.3, marginTop: 4 }}>{rp.title}</h4>
              <p className="mt-1" style={{ fontSize: "0.8rem", color: "var(--ink3)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{rp.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ── Main Component ── */

export default function BlogDetail() {
  const { postId } = useParams();
  const pageRef = useRef(null);
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [postId]);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/${postId}`);
        setPost(res.data);
        const all = await axios.get(`${API}/blog`);
        const others = all.data.filter((p) => p.id !== postId);
        const sameCat = others.filter((p) => p.category === res.data.category);
        const picks = sameCat.length >= 2 ? sameCat.slice(0, 2) : [...sameCat, ...others.filter((p) => p.category !== res.data.category)].slice(0, 2);
        setRelated(picks);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [postId]);

  if (loading) {
    return (
      <div style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink)", minHeight: "100vh", background: "var(--white)" }}>
        <SharedNav scrolled={scrolled} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <div className="flex items-center justify-center py-32">
          <div style={{ width: 40, height: 40, border: "3px solid var(--div)", borderTopColor: "var(--green)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
        <SharedFooter />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink)", minHeight: "100vh", background: "var(--white)" }}>
        <SharedNav scrolled={scrolled} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <div className="text-center py-32" data-testid="blog-detail-error">
          <p className="hm-display text-2xl">Article not found</p>
          <Link to="/blog" className="hm-btn-ghost mt-6 inline-flex" style={{ textDecoration: "none" }}>
            <ArrowLeft size={14} /> Back to Updates
          </Link>
        </div>
        <SharedFooter />
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page" ref={pageRef} style={{ fontFamily: "'DM Sans', sans-serif", color: "var(--ink)", minHeight: "100vh", background: "var(--white)" }}>
      <SEO
        title={`${post.title} — Tulsi Giri`}
        description={post.excerpt || ""}
        path={`/blog/${postId}`}
        type="article"
        image={post.image_url}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org", "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt || "",
            "image": post.image_url || "https://www.tulsigiri.com/og-image.jpg",
            "author": { "@type": "Person", "name": "Tulsi Giri", "url": "https://www.tulsigiri.com" },
            "publisher": { "@type": "Person", "name": "Tulsi Giri" },
            "datePublished": post.date || post.created_at || "",
            "dateModified": post.date || post.created_at || "",
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.tulsigiri.com/blog/${postId}` },
          })}
        </script>
      </SEO>

      <SharedNav scrolled={scrolled} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <ArticleHeader post={post} postId={postId} />

      {post.image_url && (
        <div className="max-w-4xl mx-auto px-6 -mt-2 mb-8" data-testid="blogd-hero-img">
          <img src={post.image_url} alt={post.title} style={{ width: "100%", borderRadius: 12, maxHeight: 420, objectFit: "cover" }} />
        </div>
      )}

      <ArticleBody post={post} />

      <section className="py-10 md:py-14" style={{ background: "var(--sky)" }} data-testid="blogd-bottom">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 mb-8" style={{ color: "var(--ink3)", textDecoration: "none", fontSize: "0.85rem" }} data-testid="blogd-back-bottom">
            <ArrowLeft size={14} /> Back to all updates
          </Link>
          <RelatedPosts related={related} />
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}
